import { ConfiguredRedirectUri } from 'auth/auth-components/OAuthRedirectPage';
import { SecurityAccessFeaturesEnum } from 'bff';
import {
  FeatureToggleRoute,
  useFeature
} from 'modules/app/components/atoms/feature-flags/useFeature';
import { AuthenticatedRoute } from 'components/molecules/authenticated-route/AuthenticatedRoute';
import { NotFound } from 'modules/app/components/pages/not-found/NotFound';
import { ComingSoon } from 'modules/app/components/pages/coming-soon/ComingSoon';
import { OtherApps } from 'modules/other-apps/OtherApps';
import { SupportPage } from 'modules/support/SupportPage';
import { AppLayout } from 'modules/app/components/templates/layouts/AppLayout';
import { AuthenticatedLayout } from 'modules/app/components/templates/layouts/AuthenticatedLayout';
import { FullScreenLayout } from 'modules/app/components/templates/layouts/FullScreenLayout';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RouteGuard } from '../components/atoms/route-guard/RouteGuard';
import { proposalRoutes } from './proposals/proposal-routes';
import { routes } from './route-paths';
import { RouteBoundary } from 'components/organisms/route-boundary/RouteBoundary';
import { dashboardRoutes } from './dashboard/dashboard-routes';
import { reportRoutes } from './reports/report-routes';
import { clientRoutes } from './client/client-routes';
import { caseTrackingRoutes } from './case-tracking/case-tracking-routes';
import { myPracticeRoutes } from './my-practice/my-practice-routes';

export const createRouter = (basename: string) => {
  const shouldShowDashboard = useFeature('showDashboard');

  return createBrowserRouter(
    [
      {
        path: routes.auth,
        element: <ConfiguredRedirectUri />
      },
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            element: <FullScreenLayout />,
            children: [
              {
                path: routes.legal,
                lazy: async () => {
                  const { LegalPage: Component } = await import('modules/legal/LegalPage');
                  return { Component };
                }
              }
            ]
          },
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={shouldShowDashboard ? routes.dashboard : routes.proposals} />
              },
              {
                path: routes.selectIntermediary,
                lazy: async () => {
                  const { SelectIntermediary } = await import(
                    'modules/select-intermediary/SelectIntermediary'
                  );

                  return {
                    Component: () => (
                      <RouteGuard
                        component={
                          <RouteBoundary>
                            <SelectIntermediary />
                          </RouteBoundary>
                        }
                        features={[
                          SecurityAccessFeaturesEnum.AllowActingAs,
                          SecurityAccessFeaturesEnum.AllowActingAsAll
                        ]}
                      />
                    )
                  };
                }
              },
              {
                path: routes.proposals,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrumb: {
                    text: 'New business',
                    navigationPath: routes.proposals
                  },
                  topLevelNavigationItem: true
                },
                children: proposalRoutes
              },
              {
                path: routes.reports,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrumb: {
                    text: 'Reporting',
                    navigationPath: routes.reports
                  },
                  topLevelNavigationItem: true
                },
                children: reportRoutes
              },
              {
                path: routes.otherApps,
                element: (
                  <FeatureToggleRoute
                    component={<OtherApps />}
                    fallback={<NotFound />}
                    feature='showOtherApps'
                  />
                ),
                handle: {
                  breadcrumb: {
                    text: 'Other apps',
                    navigationPath: routes.otherApps
                  },
                  topLevelNavigationItem: true
                }
              },
              {
                path: routes.dashboard,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrumb: {
                    text: 'Dashboard',
                    navigationPath: routes.dashboard
                  },
                  topLevelNavigationItem: true
                },
                children: dashboardRoutes
              },
              {
                path: routes.clients,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrumb: {
                    text: 'Clients',
                    navigationPath: routes.clients
                  },
                  topLevelNavigationItem: true
                },
                children: clientRoutes
              },
              {
                path: routes.caseTracking,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrumb: {
                    text: 'Tracking',
                    navigationPath: routes.caseTracking
                  },
                  topLevelNavigationItem: true
                },
                children: caseTrackingRoutes
              },
              {
                path: routes.myPractice,
                element: <AuthenticatedRoute />,
                handle: {
                  breadcrum: {
                    text: 'My practice',
                    navigationPath: routes.myPractice
                  },
                  topLevelNavigationItem: true
                },
                children: myPracticeRoutes
              },
              {
                path: routes.comingSoon,
                element: <ComingSoon />
              },
              {
                path: routes.support,
                element: <SupportPage />,
                handle: {
                  breadcrumb: {
                    text: 'Support',
                    navigationPath: routes.support
                  },
                  topLevelNavigationItem: true
                }
              },
              {
                path: '*',
                element: <NotFound />
              }
            ]
          }
        ]
      }
    ],
    { basename }
  );
};
